import { createContext, ReactElement, useEffect } from 'react';
import type { WithAuthenticatorProps } from '@aws-amplify/ui-react';
import { useProfile } from '../hooks/useProfile';
import { useSingletonSessions } from '../hooks/useSingletonSessions';
import { User } from '../types/user';

// Taken off the package's public entry point rather than deep-imported from
// dist/types/...: SignOut itself is not exported from the root, but it is the
// type of this prop, and a published path survives upgrades that an internal
// build path does not.
type SignOut = NonNullable<WithAuthenticatorProps['signOut']>;

type UserContextType = [User, SignOut];
const UserContext = createContext<UserContextType | null>(null);
UserContext.displayName = 'UserContext';

interface UserProviderProps {
  children: ReactElement;
  signOut: SignOut;
}

export default function UserProvider({ children, signOut }: UserProviderProps): ReactElement {
  const { data, error } = useProfile();

  // Proactively cache singleton sessions after user profile is loaded
  // Only call when team_id is available to prevent multiple calls
  useSingletonSessions(data?.team_id || undefined);

  useEffect(() => {
    if (error) {
      signOut({ global: true });
    }
  }, []);

  return (
    <UserContext.Provider value={[data, signOut]} {...{ children }}>
      {data ? children : null}
    </UserContext.Provider>
  );
}

export { UserContext };
export type { UserContextType };
